import { ReactNode } from "react"
import { Head, usePage } from "@inertiajs/react"
import type { PageProps } from "@/types/inertia"
import { SITE } from "@/lib/seo"

type SeoHeadProps = {
  title: string
  description: string
  /** OG/Twitter image — absolute URL or site-root-relative path. Defaults to the site OG image. */
  image?: string
  /** og:type — "website" (default) or "article". */
  type?: "website" | "article"
  /** Add a noindex,nofollow robots tag (for pages that shouldn't be indexed). */
  noindex?: boolean
  /** schema.org nodes to emit as a single JSON-LD @graph. */
  jsonLd?: Array<Record<string, unknown>>
  /** Extra tags to render inside <head> (e.g. an RSS alternate link). */
  children?: ReactNode
}

// Centralizes per-page SEO: title, description, canonical, Open Graph, Twitter
// cards, optional noindex, and JSON-LD. Use on every public page instead of a
// raw <Head>. JSON-LD is rendered as a plain script (valid in body per Google),
// which keeps it reliable under SSR.
export function SeoHead({
  title,
  description,
  image = SITE.ogImage,
  type = "website",
  noindex = false,
  jsonLd,
  children,
}: SeoHeadProps) {
  const { props, url } = usePage<PageProps>()
  const base = (props.app_url || "https://fullcast.co").replace(/\/$/, "")
  const path = (url || "/").split("?")[0].split("#")[0]
  const canonical = `${base}${path}`
  const imageUrl = image.startsWith("http") ? image : `${base}${image}`

  const graph =
    jsonLd && jsonLd.length > 0
      ? JSON.stringify({ "@context": "https://schema.org", "@graph": jsonLd }).replace(/</g, "\\u003c")
      : null

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        {noindex && <meta name="robots" content="noindex, nofollow" />}

        <meta property="og:site_name" content={SITE.name} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content={type} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={imageUrl} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
        {children}
      </Head>
      {graph && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: graph }} />
      )}
    </>
  )
}
